import { createFileRoute, Link } from "@tanstack/react-router";
import * as React from "react";
import { AlchemaLogo } from "~/components/AlchemaLogo";

export const Route = createFileRoute("/auth/forgot-password")({
  component: ForgotPasswordPage,
});

export function ForgotPasswordPage() {
  const [email, setEmail] = React.useState("");
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [success, setSuccess] = React.useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const redirectTo = typeof window !== "undefined"
        ? `${window.location.origin}/auth/reset-password`
        : "/auth/reset-password";
      const response = await fetch("/api/auth/request-password-reset", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, redirectTo }),
      });

      const payload = await response.json().catch(() => null) as { message?: string } | null;
      if (!response.ok) {
        throw new Error(payload?.message || "Unable to send reset email.");
      }

      setSuccess(payload?.message || "If this email exists in our system, check your email for the reset link.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to send reset email.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4" style={{ background: "var(--bg)" }}>
      <div className="w-full max-w-md space-y-6 animate-fade-up">
        <Link to="/" className="flex justify-center no-underline" aria-label="Alchema home">
          <AlchemaLogo style={{ fontSize: "1.5rem" }} />
        </Link>

        <div className="card p-8 space-y-6" style={{ borderRadius: "var(--radius-xl, 1.25rem)" }}>
          <div className="text-center">
            <h1 className="font-semibold tracking-tight" style={{ fontSize: "var(--text-2xl, 1.5rem)", color: "var(--text)", letterSpacing: "var(--tracking-snug, -0.015em)" }}>
              Reset your password
            </h1>
            <p className="mt-1.5 text-sm" style={{ color: "var(--text-muted)" }}>
              Enter your account email and we will send you a reset link.
            </p>
          </div>

          <form onSubmit={handleSubmit} data-testid="forgot-password-form" className="space-y-4">
            {error && (
              <div role="alert" className="rounded-lg border p-3 text-sm" style={{ borderColor: "var(--error)", background: "var(--error-subtle)", color: "var(--error)" }}>
                {error}
              </div>
            )}
            {success && (
              <div role="status" className="rounded-lg border p-3 text-sm" style={{ borderColor: "var(--success)", background: "var(--success-subtle, rgba(22, 163, 74, 0.1))", color: "var(--success)" }}>
                {success}
              </div>
            )}

            <div className="space-y-1.5">
              <label htmlFor="email" className="block text-xs font-medium" style={{ color: "var(--text-secondary)" }}>
                Email
              </label>
              <input
                id="email"
                type="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="input"
                placeholder="you@example.com"
              />
            </div>

            <button type="submit" disabled={loading} className="btn btn-accent w-full disabled:opacity-50">
              {loading ? "Sending reset link..." : "Send reset link"}
            </button>
          </form>
        </div>

        <p className="text-center text-sm" style={{ color: "var(--text-muted)" }}>
          Remembered your password?{" "}
          <Link to="/auth/login" className="font-medium hover:underline" style={{ color: "var(--text)" }}>
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
